import React, { useContext, useEffect, useState, Fragment } from "react";
import Controls from "../../components/controls/Controls";
import { useTheme, useMediaQuery, CircularProgress } from "@material-ui/core";
import { Paper, Toolbar } from "@material-ui/core";
import axios from "axios";
import { baseUrl } from "../../config/const";
import { AuthContext } from "../../AppContext";

import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const SalesCharts = ({ dateRanges }) => {
  const theme = useTheme();
  const matchSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [chartData, setChartData] = useState([]);
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setLoading] = useState(false);

  const ALL_VALUE = "__ALL__";
  

  const { state } = useContext(AuthContext);

const [filteringContent, setFilteringContent] = useState({
  toko: ALL_VALUE,
});

  // ===============================
  // FETCH DATA
  // ===============================
  const getSalesRecords = async (toko = "") => {
    if (!dateRanges[1]) return;

    setLoading(true);

    try {
      const { data } = await axios.get(
        `${baseUrl}/getPjKel?toko=${toko}&startdate=${dateRanges[0].toLocaleDateString(
          "en-CA"
        )}&enddate=${dateRanges[1].toLocaleDateString("en-CA")}`
      );

      setChartData(data.records);
    } catch (error) {
      console.error("Fetch error:", error);
    }

    setLoading(false);
  };

  // ===============================
  // FILTER & GROUP DATA
  // ===============================
const getFilteredSales = () => {
  const fillMissingDates = (data) => {
  if (!data.length) return [];

  const start = new Date(data[0].name);
  const end = new Date(data[data.length - 1].name);

  const map = new Map(data.map((d) => [d.name, d.total]));

  const result = [];

  for (
    let d = new Date(start);
    d <= end;
    d.setDate(d.getDate() + 1)
  ) {
    const key = d.toISOString().slice(0, 10);

    result.push({
      name: key,
      total: map.get(key) || 0,
    });
  }

  return result;
};
  const grouped = {};

  chartData.forEach((item) => {
    const date = new Date(item.Kltgklr).toISOString().slice(0, 10);

    if (!grouped[date]) grouped[date] = [];

    grouped[date].push(item);
  });

  const result = Object.keys(grouped).map((date) => {
    return {
      name: date,
      total: grouped[date].reduce((sum, curr) => sum + curr.Klrp, 0),
    };
  });

  result.sort((a, b) => new Date(a.name) - new Date(b.name));

  setDisplayedData(fillMissingDates(result));
};

  // ===============================
  // EFFECTS
  // ===============================
  useEffect(() => {
  getSalesRecords(""); // ✅ langsung ALL
}, []);

  useEffect(() => {
    getSalesRecords(filteringContent.toko);
  }, [dateRanges, filteringContent.toko]);

  useEffect(() => {
    if (chartData.length) {
      getFilteredSales();
    }
  }, [chartData, filteringContent.toko]);

  // ===============================
  // UI
  // ===============================
  return (
    <div className="online-sales">
      <h1>Grafik Penjualan</h1>

      <Paper elevation={3} style={{ margin: "auto", position: "relative" }}>
        {isLoading && (
          <Fragment>
            <div
              style={{
                position: "absolute",
                backgroundColor: "rgba(0,0,0,0.05)",
                height: "100%",
                width: "100%",
                zIndex: 8,
                borderRadius: 5,
              }}
            />
            <div
              style={{
                position: "absolute",
                zIndex: 10,
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
              }}
            >
              <CircularProgress size={80} />
            </div>
          </Fragment>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: !matchSm ? "column" : null,
          }}
        >
          <Toolbar>
            <Controls.Label label="Pilih toko: " />

<Controls.Select
  name="toko"
  value={filteringContent.toko}
  onChange={(e) => {
    const value = e.target.value;

    setFilteringContent({
      ...filteringContent,
      toko: value,
    });

    getSalesRecords(value === ALL_VALUE ? "" : value);
  }}
  options={[
    { id: ALL_VALUE, name: "All" }, // ✅ pasti tampil
    ...state.listKasir
      .map(({ username, role }) => ({
        id: username,
        name: username,
        role,
      }))
      .filter((opt) => opt.role === "toko"),
  ]}
/>
          </Toolbar>
        </div>

        <Chart displayedData={displayedData} />

        {chartData.length ? (
          <div style={{ textAlign: "right", padding: 16 }}>
            <strong>Total Omzet</strong> : Rp{" "}
            {new Intl.NumberFormat(["ban", "id"]).format(
              chartData.reduce((a, { Klrp }) => a + Klrp, 0)
            )}
          </div>
        ) : null}
      </Paper>
    </div>
  );
};

// ===============================
// CHART
// ===============================
function YtickFormatter(value) {
  return `${parseInt(value) / 1000000} JT`;
}

function XtickFormatter(value) {
  const dateArr = new Date(value).toDateString().split(" ");
  return `${dateArr[2]} ${dateArr[1]}`;
}

function tooltipFormatter(value) {
  value = new Intl.NumberFormat(["ban", "id"]).format(parseInt(value));
  return [`Rp ${value}`, "Total"];
}

function labelFormatter(value) {
  return `${new Date(value).toDateString()}`;
}

function Chart({ displayedData }) {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart 
        data={displayedData}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
        <Line
          animationDuration={500}
          type="monotone"
          dataKey="total"
          stroke="#B80000"
        />
        <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
        <XAxis dataKey="name" tickFormatter={XtickFormatter} />
        <YAxis tickFormatter={YtickFormatter} />
        <Tooltip formatter={tooltipFormatter} labelFormatter={labelFormatter} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default SalesCharts;